import {
  formatDateFilterValue,
  matchAmountFilter,
  matchGymIdFilter,
  matchTextFilter,
  normalizeDateFilterToIso,
} from '../../../components/admin/ColumnFilterHeader';
import { formatCurrency } from '../receptionistData';
import {
  saleRowDuration,
  saleRowEndDate,
  saleRowPackageName,
  saleRowStartDate,
  saleRowTrainerName,
  isTrainerSaleSource,
} from './salePeriodUtils';
import {
  saleRowLeft,
  saleRowPaid,
  saleRowTotal,
} from './salePaymentUtils';
import {
  matchSaleMethodFilters,
  saleMethodFilterDefaults,
} from './SaleMethodTableColumns';

/** Empty-string defaults so column filters behave like Memberships text search. */
export function saleHistoryFilterDefaults(extra = {}) {
  return {
    date: '',
    gymId: '',
    member: '',
    item: '',
    renewal: '',
    duration: '',
    total: '',
    totalPaid: '',
    totalLeft: '',
    startDate: '',
    endDate: '',
    note: '',
    ...saleMethodFilterDefaults(),
    ...extra,
  };
}

function toDmyDisplay(isoOrDate) {
  const raw = String(isoOrDate || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  return `${raw.slice(8, 10)}-${raw.slice(5, 7)}-${raw.slice(0, 4)}`;
}

/** Date column filter: supports DD-MM-YYYY typing (like Memberships) and ISO contains. */
export function matchSaleHistoryDateFilter(dateValue, filter) {
  const needle = String(filter || '').trim();
  if (!needle || needle === 'all') return true;
  const raw = String(dateValue || '').slice(0, 10);
  if (!raw || raw === '—') return false;
  const iso = normalizeDateFilterToIso(needle);
  if (iso) return raw === iso;
  return (
    matchTextFilter(raw, needle)
    || matchTextFilter(toDmyDisplay(raw), needle)
    || matchTextFilter(raw.replace(/-/g, ''), needle.replace(/\D/g, ''))
  );
}

export { formatDateFilterValue };

/** Inclusive sale-date range filter (YYYY-MM-DD). Empty bounds = no limit. */
export function matchSaleDateRange(dateValue, startDate, endDate) {
  const start = String(startDate || '').trim().slice(0, 10);
  const end = String(endDate || '').trim().slice(0, 10);
  if (!start && !end) return true;
  const d = String(dateValue || '').slice(0, 10);
  if (!d || d === '—') return false;
  if (start && d < start) return false;
  if (end && d > end) return false;
  return true;
}

/**
 * Client-side history filter matching Memberships-style text / amount / date search.
 * @param {object} row payment/sale row
 * @param {object} filters column filter state
 * @param {{ itemKey?: 'item' | 'renewal', dateFn?: (row) => string }} [opts]
 */
export function matchSaleHistoryFilters(row, filters, opts = {}) {
  const itemKey = opts.itemKey || 'item';
  const dateFn = opts.dateFn || ((r) => String(r.date || '').slice(0, 10));
  const itemValue = itemKey === 'renewal'
    ? saleRowPackageName(row)
    : (isTrainerSaleSource(row.source) ? saleRowTrainerName(row) : saleRowPackageName(row));
  const rangeStart = opts.rangeStart ?? filters.rangeStart;
  const rangeEnd = opts.rangeEnd ?? filters.rangeEnd;

  return (
    matchSaleDateRange(dateFn(row), rangeStart, rangeEnd)
    && matchSaleHistoryDateFilter(dateFn(row), filters.date)
    && matchGymIdFilter(row.gymId, filters.gymId)
    && matchTextFilter(row.member, filters.member)
    && matchTextFilter(itemValue, filters[itemKey])
    && matchTextFilter(saleRowDuration(row), filters.duration)
    && matchAmountFilter(saleRowTotal(row), filters.total, formatCurrency)
    && matchAmountFilter(saleRowPaid(row), filters.totalPaid, formatCurrency)
    && matchAmountFilter(saleRowLeft(row), filters.totalLeft, formatCurrency)
    && matchSaleHistoryDateFilter(saleRowStartDate(row), filters.startDate)
    && matchSaleHistoryDateFilter(saleRowEndDate(row), filters.endDate)
    && matchTextFilter(row.paymentNote, filters.note)
    && matchSaleMethodFilters(row, filters)
  );
}
